import React, { useState, useEffect } from 'react';
import {
  Show,
  RichTextField,
  useRecordContext,
  useRedirect,
  useGetList,
  useUpdate,
  useNotify,
  useDelete,
  useDataProvider,
  useRefresh,
} from 'react-admin';
import ResourceBreadcrumbsWithActions from '../../components/ResourceBreadcrumbsWithActions';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Box,
  Avatar,
  Divider,
  IconButton,
  Button as MuiButton,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField as MuiTextField,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Container,
} from '@mui/material';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  RequestQuote as BillIcon,
  Receipt as InvoiceIcon,
  TrendingUp as OpportunityIcon,
  Edit as EditIcon,
  ContactMail as ContactIcon,
  AccountBalance as AccountIcon,
  LocalOffer as TagIcon,
  Assignment as TaskIcon,
  Close as CloseIcon,
  Notes as NotesIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  AccountTree as HierarchyIcon,
  Loop as LoopIcon,
} from '@mui/icons-material';
import { CompanyInfoDialog } from '../../components/CompanyInfoDialog';
import { ContactInfoDialog } from '../../components/ContactInfoDialog';
import { LocationZoneDialog } from '../../components/LocationZoneDialog';
import { AddressTable } from '../../components/AddressTable';
import { ContactLogList } from '../../components/ContactLogList';
import { CreateOpportunityDialog } from '../../components/CreateOpportunityDialog';
import { getCustomerTypeColor } from '../../utils/customerTypeColors';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import { isVirtualTask } from '../../utils/taskUtils';
import { CustomerPortalTab } from './CustomerPortalTab';
import { TaskDialog } from '../../components/TaskDialog';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const CustomerContactInformationCard = ({ contactDialogOpen, setContactDialogOpen }: { contactDialogOpen: boolean, setContactDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Contact Information
          </Typography>
          <IconButton
            size="small"
            onClick={() => setContactDialogOpen(true)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={3}>
          {/* Name Section - Two Columns */}
          {(record.firstName || record.lastName) && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Name
              </Typography>
              <Grid container spacing={2}>
                {record.firstName && (
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                        First Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {record.firstName}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {record.lastName && (
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                        Last Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {record.lastName}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Job Title */}
          {record.title && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, fontWeight: 600 }}>
                Job Title
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {record.title}
              </Typography>
            </Box>
          )}

          {/* Contact Methods */}
          {record.phone && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Contact Methods
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                      <PhoneIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                      Phone
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {record.phone}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const CustomerCompanyInformationCard = ({ companyDialogOpen, setCompanyDialogOpen }: { companyDialogOpen: boolean, setCompanyDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [parentDialogOpen, setParentDialogOpen] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(record?.parentCustomerId || null);
  const [availableParents, setAvailableParents] = useState<any[]>([]);
  const [loadingParents, setLoadingParents] = useState(false);

  useEffect(() => {
    if (parentDialogOpen) {
      loadAvailableParents();
    }
  }, [parentDialogOpen]);

  const loadAvailableParents = async () => {
    try {
      setLoadingParents(true);
      const response = await dataProvider.getList('customers', {
        filter: { id_ne: record?.id },
        sort: { field: 'displayName', order: 'ASC' },
        pagination: { page: 1, perPage: 1000 },
      });
      setAvailableParents(response.data);
    } catch (error) {
      notify('Failed to load customers', { type: 'error' });
    } finally {
      setLoadingParents(false);
    }
  };

  const handleSaveParent = async () => {
    try {
      await dataProvider.update('customers', {
        id: record.id,
        data: { parentCustomerId: selectedParentId },
        previousData: record,
      });
      notify('Parent customer updated successfully', { type: 'success' });
      setParentDialogOpen(false);
      refresh();
    } catch (error) {
      notify('Failed to update parent customer', { type: 'error' });
    }
  };

  // Always render the card, even if record is not yet loaded
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Company Information
          </Typography>
          <IconButton
            size="small"
            onClick={() => setCompanyDialogOpen(true)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={3}>
          {/* Company Name */}
          {record?.company && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, fontWeight: 600 }}>
                <BusinessIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                Company Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {record?.company}
              </Typography>
            </Box>
          )}

          {/* Business Details */}
          {(record?.zone?.name || record?.customerType?.name) && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Business Details
              </Typography>
              <Grid container spacing={2}>
                {record?.zone?.name && (
                  <Grid item xs={record?.customerType?.name ? 6 : 12}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                        Service Zone
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {record?.zone?.name}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {record?.customerType?.name && (
                  <Grid item xs={record?.zone?.name ? 6 : 12}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                        Customer Type
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {record?.customerType?.name}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Parent/Child Relationships */}
          <Box sx={{ p: 2, bgcolor: 'blue.50', borderRadius: 1, border: '1px solid', borderColor: 'blue.200' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                <HierarchyIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                Organization Hierarchy
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedParentId(record?.parentCustomerId || null);
                  setParentDialogOpen(true);
                }}
                sx={{ p: 0.5 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            {record?.parentCustomer ? (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Parent Organization:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'primary.main', cursor: 'pointer' }}
                  onClick={() => window.location.href = `/customers/${record.parentCustomer.id}/show`}>
                  {record.parentCustomer.displayName || record.parentCustomer.company || 'Parent Customer'}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No parent organization set
              </Typography>
            )}
            {record?.childCustomers?.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Child Organizations ({record.childCustomers.length}):</Typography>
                {record.childCustomers.map((child: any) => (
                  <Typography key={child.id} variant="body2" sx={{ ml: 2, color: 'primary.main', cursor: 'pointer' }}
                    onClick={() => window.location.href = `/customers/${child.id}/show`}>
                    • {child.displayName || child.company || 'Child Customer'}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          {/* Parent Customer Edit Dialog */}
          <Dialog open={parentDialogOpen} onClose={() => setParentDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Parent Organization</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Select a parent organization for this customer. This creates a hierarchical relationship.
              </DialogContentText>
              {loadingParents ? (
                <Typography>Loading customers...</Typography>
              ) : (
                <MuiTextField
                  select
                  fullWidth
                  label="Parent Customer"
                  value={selectedParentId || ''}
                  onChange={(e) => setSelectedParentId(e.target.value || null)}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="">No parent (independent organization)</option>
                  {availableParents.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.displayName || customer.company || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Unknown'}
                    </option>
                  ))}
                </MuiTextField>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setParentDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveParent} variant="contained" disabled={loadingParents}>
                Save
              </Button>
            </DialogActions>
          </Dialog>


        </Stack>
      </CardContent>
    </Card>
  );
};

const AddressTableWrapper = () => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }
  return <AddressTable customerId={String(record.id)} />;
};

const ContactLogTableWrapper = () => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return <ContactLogList customerId={String(record.id)} />;
};

// Actions are now integrated into ResourceBreadcrumbsWithActions

const LotSizeField = ({ record }: { record?: { lotSize?: string } & Record<string, unknown> }) => {
  if (!record?.lotSize) {
    return <span>-</span>;
  }

  const getLotSizeColor = (size: string) => {
    switch (size) {
      case 'Small':
        return 'default';
      case 'Medium':
        return 'primary';
      case 'Large':
        return 'secondary';
      case 'XLarge':
        return 'warning';
      case 'Commercial':
        return 'success';
      default:
        return 'default';
    }
  };

  return <Chip label={record.lotSize} size="small" color={getLotSizeColor(record.lotSize)} />;
};

const CustomerSummaryCard = ({
  companyDialogOpen,
  setCompanyDialogOpen,
  onDisplayNameEdit,
}: {
  companyDialogOpen: boolean;
  setCompanyDialogOpen: (open: boolean) => void;
  onDisplayNameEdit: () => void;
}) => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const notify = useNotify();
  const customerTypeColor = record?.customerType?.color || getCustomerTypeColor(record?.customerType?.name);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteOne, { isLoading: isDeleting }] = useDelete('customers', { id: record?.id });

  if (!record) {
    return null;
  }

  const getInitials = (name: string) => {
    return name
      ? name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
      : '?';
  };

  const displayName =
    record.displayName ||
    `${record.firstName || ''} ${record.lastName || ''}`.trim() ||
    record.company ||
    'Unknown Customer';

  const handleDelete = async () => {
    try {
      await deleteOne();
      notify('Customer deleted successfully', { type: 'success' });
      redirect('/customers');
    } catch (error) {
      notify('Error deleting customer', { type: 'error' });
    }
    setDeleteDialogOpen(false);
  };

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: customerTypeColor,
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              {record.company ? <BusinessIcon sx={{ fontSize: 40, color: 'white' }} /> : getInitials(displayName)}
            </Avatar>
          </Grid>

          <Grid item xs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: customerTypeColor }}>
                {displayName}
              </Typography>
              <IconButton
                size="small"
                onClick={onDisplayNameEdit}
                sx={{
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              {record.company && (
                <Chip
                  icon={<BusinessIcon />}
                  label={record.company}
                  variant="outlined"
                  color="primary"
                />
              )}
              {record.lotSize && <LotSizeField record={record} />}
              {record.customerType && (
                <Chip
                  icon={<TagIcon />}
                  label={record.customerType.name || record.customerType}
                  variant="outlined"
                  color="secondary"
                />
              )}
            </Stack>

            <Grid container spacing={2}>
              {record.phone && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {record.phone}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {(record.address || record.city || record.state) && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {[record.address, record.city, record.state].filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item>
            <MuiButton
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              size="small"
            >
              Delete Customer
            </MuiButton>
          </Grid>
        </Grid>
      </CardContent>

      {record && (
        <CompanyInfoDialog
          open={companyDialogOpen}
          onClose={() => setCompanyDialogOpen(false)}
          record={record}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Customer
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{displayName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const QuickStatsCard = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [stats, setStats] = useState([
    { label: 'Total Invoices', value: '...', color: 'primary', icon: <BillIcon /> },
    { label: 'Total Revenue', value: '...', color: 'success', icon: <AccountIcon /> },
    { label: 'Open Opportunities', value: '...', color: 'warning', icon: <OpportunityIcon /> },
    { label: 'Last Activity', value: '...', color: 'info', icon: <ContactIcon /> },
  ]);
  const [invoicesData, setInvoicesData] = useState<any[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Fetch invoices from customer-specific endpoint
  useEffect(() => {
    if (!record?.id) {
      setInvoicesData([]);
      return;
    }

    const fetchInvoices = async () => {
      setInvoicesLoading(true);
      try {
        const response = await dataProvider.getList(`crm/invoices/customer/${record.id}`, {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'createdAt', order: 'DESC' },
        });
        setInvoicesData(response.data);
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setInvoicesData([]);
      } finally {
        setInvoicesLoading(false);
      }
    };

    fetchInvoices();
  }, [record?.id, dataProvider]);

  // Fetch opportunities
  const { data: opportunitiesData, isLoading: opportunitiesLoading } = useGetList(
    'opportunities',
    {
      filter: { customerId: record?.id },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'createdAt', order: 'DESC' },
    },
    { enabled: !!record?.id }
  );

  // Fetch contact logs for last activity
  const { data: contactLogsData, isLoading: contactLogsLoading } = useGetList(
    'contact-logs',
    {
      filter: { customerId: record?.id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'contactDate', order: 'DESC' },
    },
    { enabled: !!record?.id }
  );

  useEffect(() => {
    if (!record) {
      return;
    }

    const isLoading = invoicesLoading || opportunitiesLoading || contactLogsLoading;
    if (isLoading) {
      return;
    }

    // Calculate total invoices
    const totalInvoices = invoicesData?.length || 0;

    // Calculate total revenue (sum of all invoice amounts)
    const totalRevenue = (invoicesData || []).reduce(
      (sum: number, invoice: { totalAmount?: string | number }) => {
        return sum + (parseFloat(String(invoice.totalAmount)) || 0);
      },
      0
    );

    // Count open opportunities
    const openOpportunities = (opportunitiesData || []).filter(
      (opp: { stage?: { name?: string } }) => {
        const stage = opp.stage?.name?.toLowerCase();
        return stage && !['closed won', 'closed lost', 'cancelled'].includes(stage);
      }
    ).length;

    // Get last activity
    const lastActivity =
      contactLogsData && contactLogsData.length > 0
        ? getTimeAgo(contactLogsData[0].contactDate)
        : 'No activity';

    setStats([
      {
        label: 'Total Invoices',
        value: totalInvoices.toString(),
        color: 'primary',
        icon: <BillIcon />,
      },
      {
        label: 'Total Revenue',
        value: formatCurrency(totalRevenue),
        color: 'success',
        icon: <AccountIcon />,
      },
      {
        label: 'Open Opportunities',
        value: openOpportunities.toString(),
        color: 'warning',
        icon: <OpportunityIcon />,
      },
      { label: 'Last Activity', value: lastActivity, color: 'info', icon: <ContactIcon /> },
    ]);
  }, [
    record,
    invoicesData,
    opportunitiesData,
    contactLogsData,
    invoicesLoading,
    opportunitiesLoading,
    contactLogsLoading,
  ]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return '1 day ago';
    }
    if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    if (diffDays < 30) {
      return `${Math.ceil(diffDays / 7)} weeks ago`;
    }
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  if (!record) {
    return null;
  }

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {stats.map((stat, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              {React.cloneElement(stat.icon, {
                color: stat.color as 'primary' | 'success' | 'warning' | 'info',
              })}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {stat.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

const EstimatesTabContent = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [estimates, setEstimates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;

    const fetchEstimates = async () => {
      try {
        const response = await dataProvider.getList(`crm/bills/customer/${record.id}`, {
          pagination: { page: 1, perPage: 25 },
          sort: { field: 'createdAt', order: 'DESC' },
        });
        setEstimates(response.data);
      } catch (error) {
        console.error('Error fetching estimates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEstimates();
  }, [record?.id, dataProvider]);

  return (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <BillIcon color="primary" />
          Estimates
          <MuiButton
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={{ ml: 'auto' }}
            onClick={() => redirect('/estimates/create?customerId=' + String(record?.id))}
          >
            Create Estimate
          </MuiButton>
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Typography>Loading estimates...</Typography>
        ) : estimates.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No estimates found for this customer
          </Typography>
        ) : (
          <Box sx={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Estimate #</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Total</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {estimates.map((estimate: any) => (
                  <tr
                    key={estimate.id}
                    onClick={() => redirect(`/bills/${estimate.id}/show`)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '12px' }}>{estimate.billNumber}</td>
                    <td style={{ padding: '12px' }}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      }).format(estimate.totalAmount || 0)}
                    </td>
                    <td style={{ padding: '12px' }}>{estimate.state || 'Draft'}</td>
                    <td style={{ padding: '12px' }}>
                      {estimate.createdAt ? new Date(estimate.createdAt).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const InvoicesTabContent = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;

    const fetchInvoices = async () => {
      try {
        const response = await dataProvider.getList(`crm/invoices/customer/${record.id}`, {
          pagination: { page: 1, perPage: 25 },
          sort: { field: 'createdAt', order: 'DESC' },
        });
        setInvoices(response.data);
      } catch (error) {
        console.error('Error fetching invoices:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [record?.id, dataProvider]);

  return (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <InvoiceIcon color="primary" />
          Invoices
          <MuiButton
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={{ ml: 'auto' }}
            onClick={() => redirect('/invoices/create?customerId=' + String(record?.id))}
          >
            Create Invoice
          </MuiButton>
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Typography>Loading invoices...</Typography>
        ) : invoices.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No invoices found for this customer
          </Typography>
        ) : (
          <Box sx={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Roboto, Arial, sans-serif' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#666', fontFamily: 'inherit' }}>Invoice #</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#666', fontFamily: 'inherit' }}>Total</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#666', fontFamily: 'inherit' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#666', fontFamily: 'inherit' }}>Due Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#666', fontFamily: 'inherit' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice: any) => (
                  <tr
                    key={invoice.id}
                    onClick={() => redirect(`/invoices/${invoice.id}/show`)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '12px' }}>{invoice.invoiceNumber}</td>
                    <td style={{ padding: '12px' }}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      }).format(invoice.totalAmount || 0)}
                    </td>
                    <td style={{ padding: '12px' }}>{invoice.state || 'Draft'}</td>
                    <td style={{ padding: '12px' }}>
                      {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Custom TasksTable component to avoid DatagridHeader issues
const TasksTable = ({ customerId, startDate, endDate, onTaskClick }: { customerId?: string; startDate?: Date | null; endDate?: Date | null; onTaskClick: (id: string, data: any) => Promise<boolean> }) => {
  const [error, setError] = useState<string | null>(null);
  const redirect = useRedirect();

  // Use React Admin's useGetList hook instead of direct data provider calls
  const { data: tasksData = [], isLoading, error: queryError } = useGetList(
    'tasks',
    {
      filter: {
        customerId: customerId,
        startDate: startDate && !isNaN(startDate.getTime()) ? startDate.toISOString() : undefined,
        endDate: endDate && !isNaN(endDate.getTime()) ? endDate.toISOString() : undefined
      },
      sort: { field: 'scheduledDate', order: 'ASC' },
      pagination: { page: 1, perPage: 25 }
    },
    {
      enabled: !!customerId // Only fetch when customerId is available
    }
  );

  useEffect(() => {
    if (queryError) {
      setError('Failed to load tasks');
      console.error('Error fetching tasks:', queryError);
    } else {
      setError(null);
    }
  }, [queryError]);

  if (isLoading) {
    return <Typography>Loading tasks...</Typography>;
  }

  if (error) {
    return (
      <Box sx={{
        textAlign: 'center',
        py: 4,
        color: 'text.secondary'
      }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!customerId || tasksData.length === 0) {
    return (
      <Box sx={{
        textAlign: 'center',
        py: 4,
        color: 'text.secondary',
        bgcolor: 'grey.50',
        borderRadius: 1,
        border: '1px dashed',
        borderColor: 'grey.300'
      }}>
        <TaskIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          No tasks found
        </Typography>
        <Typography variant="body2">
          Tasks for this customer will appear here
        </Typography>
      </Box>
    );
  }

  return (
    <Table size="medium">
      <TableHead>
        <TableRow>
          <TableCell>
            <Typography variant="subtitle2" color="text.secondary">
              Task
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" color="text.secondary">
              Priority
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" color="text.secondary">
              Assigned to
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" color="text.secondary">
              Scheduled
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" color="text.secondary">
              Created
            </Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {tasksData.map((task: any) => (
          <TableRow
            key={task.id}
            onClick={() => redirect(`/tasks/${task.id}/show`)}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <TableCell>
              <Typography variant="body2">
                {task.title}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip
                label={task.priority || 'Medium'}
                size="small"
                color={
                  task.priority === 'Urgent' ? 'error' :
                    task.priority === 'High' ? 'warning' :
                      task.priority === 'Low' ? 'success' : 'primary'
                }
              />
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {task.assignees && task.assignees.length > 0
                  ? task.assignees.map((a: any) => a.name).join(', ')
                  : task.assignee?.name || '-'}
              </Typography>
            </TableCell>
            <TableCell>
              {(() => {
                const sd = task.scheduledDate;
                if (!sd) { return <Typography variant="body2">-</Typography>; }
                const scheduledDate = new Date(sd);
                if (isNaN(scheduledDate.getTime())) { return <Typography variant="body2">-</Typography>; }

                const yyyy = scheduledDate.getFullYear();
                const mm = String(scheduledDate.getMonth() + 1).padStart(2, '0');
                const dd = String(scheduledDate.getDate()).padStart(2, '0');
                const datePart = `${yyyy}-${mm}-${dd}`;

                const timePart = scheduledDate.toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                });

                const rawDuration = task.estimatedHours;
                const durationPart = rawDuration !== null && rawDuration !== undefined
                  ? `, ${Number.isInteger(rawDuration) ? rawDuration : parseFloat(rawDuration.toFixed(2))}h`
                  : '';

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" component="span">
                        {datePart}, {timePart}{durationPart}
                      </Typography>
                      {isVirtualTask(task) && (
                        <Tooltip title="Recurring Task Instance">
                          <LoopIcon fontSize="inherit" color="action" sx={{ fontSize: '0.9rem' }} />
                        </Tooltip>
                      )}
                    </Box>
                    {(!isVirtualTask(task) && task.recurrenceFrequency) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <LoopIcon fontSize="inherit" color="primary" sx={{ fontSize: '0.8rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {task.recurrenceFrequency}
                          {task.recurrenceEndDate && `, Ends ${new Date(task.recurrenceEndDate).toLocaleDateString()}`}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {task.createdAt ? new Date(task.createdAt).toLocaleDateString() : '-'}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const TasksTabContent = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // Helper to get default date range (Current Year)
  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), 0, 1), // Jan 1st
      end: new Date(now.getFullYear(), 11, 31), // Dec 31st
    };
  };

  const defaultDateRange = getDefaultDateRange();
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>(defaultDateRange);

  const handleTaskClick = async (id: string, data: any) => {
    try {
      // Fetch the full task details
      const { data: fullTask } = await dataProvider.getOne('tasks', { id });
      setSelectedTask(fullTask);

      // Fetch the workflow for this task
      if (fullTask.workflowId) {
        try {
          const { data: workflow } = await dataProvider.getOne('workflows', { id: fullTask.workflowId });
          setSelectedWorkflow(workflow);
        } catch (error) {
          console.error('Failed to fetch workflow:', error);
        }
      }
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch task details:', error);
    }
    return false;
  };

  const handleSwitchTask = (newTask: any) => {
    setEditDialogOpen(false);
    setTimeout(() => {
      setSelectedTask(newTask);
      setEditDialogOpen(true);
    }, 300);
  };

  return (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <TaskIcon color="primary" />
          Tasks
          <MuiButton
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={{ ml: 'auto' }}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Task
          </MuiButton>
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction="row" spacing={2} alignItems="center">
              <DatePicker
                label="Start Date"
                value={dateRange.start}
                onChange={(newValue) => {
                  // Only enforce default if explicitly cleared (null), not if invalid (typing)
                  if (newValue === null) {
                    setDateRange(prev => ({ ...prev, start: getDefaultDateRange().start }));
                  } else {
                    setDateRange(prev => ({ ...prev, start: newValue }));
                  }
                }}
                slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
              />
              <DatePicker
                label="End Date"
                value={dateRange.end}
                onChange={(newValue) => {
                  // Only enforce default if explicitly cleared (null), not if invalid (typing)
                  if (newValue === null) {
                    setDateRange(prev => ({ ...prev, end: getDefaultDateRange().end }));
                  } else {
                    setDateRange(prev => ({ ...prev, end: newValue }));
                  }
                }}
                slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
              />
            </Stack>
          </LocalizationProvider>
        </Box>

        <TasksTable
          customerId={record?.id ? String(record.id) : undefined}
          startDate={dateRange.start}
          endDate={dateRange.end}
          onTaskClick={handleTaskClick}
        />

        {/* Task Dialog (Create & Edit) */}
        <TaskDialog
          open={createDialogOpen || editDialogOpen}
          onClose={() => {
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
            setSelectedTask(null);
            setSelectedWorkflow(null);
          }}
          task={editDialogOpen ? selectedTask : undefined}
          customerId={record?.id ? String(record.id) : undefined}
          onSuccess={() => {
            refresh();
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
            setSelectedTask(null);
            setSelectedWorkflow(null);
          }}
          onDelete={() => {
            refresh();
            setEditDialogOpen(false);
            setSelectedTask(null);
            setSelectedWorkflow(null);
          }}
          onSwitchTask={handleSwitchTask}
        />
      </CardContent>
    </Card>
  );
};

const CustomerLocationZoneCard = ({ locationZoneDialogOpen, setLocationZoneDialogOpen }: { locationZoneDialogOpen: boolean, setLocationZoneDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Location & Zone
          </Typography>
          <IconButton
            size="small"
            onClick={() => setLocationZoneDialogOpen(true)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={3}>
          {/* Zone */}
          {record.zone?.name && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, fontWeight: 600 }}>
                <LocationIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                Service Zone
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {record.zone.name}
              </Typography>
            </Box>
          )}

          {/* Customer Type */}
          {record.customerType?.name && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, fontWeight: 600 }}>
                <TagIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                Customer Type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {record.customerType.name}
              </Typography>
            </Box>
          )}

          {/* Show placeholder if no data */}
          {!record.zone?.name && !record.customerType?.name && (
            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 1, border: '1px dashed', borderColor: 'grey.300', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No location or zone information available
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const NotesDialog = ({ open, onClose, record }: { open: boolean; onClose: () => void; record: any }) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    notes: record?.notes || '',
  });

  // Update form data when record changes
  React.useEffect(() => {
    if (record) {
      setFormData({
        notes: record?.notes || '',
      });
    }
  }, [record]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      notes: event.target.value,
    }));
  };

  const handleSave = async () => {
    if (!record) return;

    try {
      await update('customers', {
        id: record.id,
        data: formData,
        previousData: record,
      });
      notify('Customer notes updated successfully', { type: 'success' });
      onClose();
    } catch (error) {
      notify('Error updating customer notes', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotesIcon color="primary" />
            <Typography variant="h6">Customer Notes</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <MuiTextField
            fullWidth
            label="Notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={10}
            placeholder="Add notes about this customer..."
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DisplayNameDialog = ({ open, onClose, value, onChange, onSave }: {
  open: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            Edit Display Name
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <MuiTextField
            fullWidth
            label="Display Name"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter display name for this customer..."
            autoFocus
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OpportunityCards = ({ customerId, onOpportunityChange }: { customerId?: string; onOpportunityChange?: () => void }) => {
  const redirect = useRedirect();
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpportunityOpen, setCreateOpportunityOpen] = useState(false);
  const dataProvider = useDataProvider();

  useEffect(() => {
    if (!customerId) return;

    const fetchOpportunities = async () => {
      try {
        const response = await dataProvider.getList('opportunities', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'createdAt', order: 'DESC' },
          filter: { customerId },
        });

        setOpportunities(response.data);
      } catch (error) {
        console.error('Error fetching opportunities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunities();
  }, [customerId, dataProvider]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="primary" gutterBottom>
            Opportunities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Loading opportunities...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Opportunities ({opportunities.length})
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {opportunities.length > 0 && (
              <Chip
                label={`$${opportunities.reduce((sum, opp) => sum + (opp.value || 0), 0).toLocaleString()}`}
                color="primary"
                size="small"
                icon={<MoneyIcon />}
              />
            )}
            <Tooltip title="Create opportunity">
              <IconButton
                size="small"
                color="primary"
                onClick={() => setCreateOpportunityOpen(true)}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {opportunities.length === 0 ? (
          <Box sx={{
            textAlign: 'center',
            py: 4,
            color: 'text.secondary',
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'grey.300'
          }}>
            <OpportunityIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              No opportunities yet
            </Typography>
            <Typography variant="body2">
              Opportunities will appear here when they're created for this customer
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {opportunities.map((opportunity) => (
              <Grid item xs={12} sm={6} lg={4} key={opportunity.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => redirect(`/opportunities/${opportunity.id}/show`)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        {opportunity.name}
                      </Typography>
                      <Chip
                        label={opportunity.stage?.isClosedWon ? 'Won' : opportunity.stage?.isClosedLost ? 'Lost' : 'Open'}
                        size="small"
                        color={opportunity.stage?.isClosedWon ? 'success' : opportunity.stage?.isClosedLost ? 'error' : 'primary'}
                      />
                    </Box>

                    <Typography variant="h5" sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 1
                    }}>
                      ${opportunity.value?.toLocaleString() || '0'}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {opportunity.probability !== null && (
                        <Chip
                          label={`${opportunity.probability}%`}
                          size="small"
                          color={opportunity.probability >= 75 ? 'success' : opportunity.probability >= 50 ? 'warning' : 'default'}
                        />
                      )}
                      {opportunity.stage?.name && (
                        <Chip
                          label={opportunity.stage.name}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {opportunity.expectedCloseDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Expected: {new Date(opportunity.expectedCloseDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Create Opportunity Dialog */}
        <CreateOpportunityDialog
          open={createOpportunityOpen}
          onClose={() => {
            setCreateOpportunityOpen(false);
            // Re-fetch opportunities after dialog closes
            if (customerId) {
              const fetchOpportunities = async () => {
                try {
                  const response = await dataProvider.getList('opportunities', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'createdAt', order: 'DESC' },
                    filter: { customerId },
                  });
                  setOpportunities(response.data);
                  // Notify parent about the change
                  if (onOpportunityChange) {
                    onOpportunityChange();
                  }
                } catch (error) {
                  console.error('Error fetching opportunities:', error);
                }
              };
              fetchOpportunities();
            }
          }}
          defaultCustomerId={customerId}
          defaultCustomerName="Customer" // This will be populated properly by the dialog
        />
      </CardContent>
    </Card>
  );
};

const CustomerShowContent = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  // Persist tab selection in localStorage
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('customerShowActiveTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [locationZoneDialogOpen, setLocationZoneDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [displayNameDialogOpen, setDisplayNameDialogOpen] = useState(false);
  const [displayNameValue, setDisplayNameValue] = useState('');
  const [estimatesCount, setEstimatesCount] = useState(0);
  const [invoicesCount, setInvoicesCount] = useState(0);
  const [opportunitiesCount, setOpportunitiesCount] = useState(0);
  const [tasksCount, setTasksCount] = useState(0);
  const [contactLogsCount, setContactLogsCount] = useState(0);
  const [addressesCount, setAddressesCount] = useState(0);

  // Function to fetch all counts
  const fetchAllCounts = async () => {
    if (!record?.id) {
      // Reset counts when no record
      setEstimatesCount(0);
      setInvoicesCount(0);
      setOpportunitiesCount(0);
      setTasksCount(0);
      setContactLogsCount(0);
      setAddressesCount(0);
      return;
    }

    try {
      // Get estimates count using customer-specific endpoint
      const estimatesResponse = await dataProvider.getList(`crm/bills/customer/${record.id}`, {
        pagination: { page: 1, perPage: 1 },
        filter: {},
      });
      setEstimatesCount(estimatesResponse.total || 0);

      // Get invoices count using customer-specific endpoint
      const invoicesResponse = await dataProvider.getList(`crm/invoices/customer/${record.id}`, {
        pagination: { page: 1, perPage: 1 },
      });
      setInvoicesCount(invoicesResponse.total || 0);

      // Get opportunities count
      const opportunitiesResponse = await dataProvider.getList('opportunities', {
        pagination: { page: 1, perPage: 1 },
        filter: { customerId: record.id },
      });
      setOpportunitiesCount(opportunitiesResponse.total || 0);

      // Get tasks count using entity-specific endpoint
      const tasksResponse = await dataProvider.getList(`tasks/entity/customer/${record.id}`, {
        pagination: { page: 1, perPage: 1 },
      });
      setTasksCount(tasksResponse.total || 0);

      // Get contact logs count
      const contactLogsResponse = await dataProvider.getList('crm/contact-logs', {
        pagination: { page: 1, perPage: 1 },
        filter: { customerId: record.id },
      });
      setContactLogsCount(contactLogsResponse.total || 0);

      // Get addresses count
      const addressesResponse = await dataProvider.getList('customer-addresses', {
        pagination: { page: 1, perPage: 1 },
        filter: { customerId: record.id },
      });
      setAddressesCount(addressesResponse.total || 0);
    } catch (error) {
      console.error('Error fetching tab counts:', error);
    }
  };

  // Fetch counts on mount and when record changes
  useEffect(() => {
    // Reset counts when record changes
    setEstimatesCount(0);
    setInvoicesCount(0);
    setOpportunitiesCount(0);
    setTasksCount(0);
    setContactLogsCount(0);
    setAddressesCount(0);

    // Fetch new counts
    fetchAllCounts();
  }, [record?.id]);

  // Refresh counts when the page regains focus (e.g., returning from create pages)
  useEffect(() => {
    const handleFocus = () => {
      fetchAllCounts();
    };

    window.addEventListener('focus', handleFocus);

    // Also listen for visibility change (for tab switching)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchAllCounts();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [record?.id]);

  const handleDisplayNameEdit = () => {
    setDisplayNameValue(record?.displayName || '');
    setDisplayNameDialogOpen(true);
  };

  const handleDisplayNameSave = async () => {
    try {
      await update('customers', {
        id: record.id,
        data: { displayName: displayNameValue },
        previousData: record,
      });

      notify('Display name updated successfully', { type: 'success' });
      refresh();
      setDisplayNameDialogOpen(false);
    } catch (error) {
      notify('Error updating display name', { type: 'error' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box>
        <CustomerSummaryCard
          companyDialogOpen={companyDialogOpen}
          setCompanyDialogOpen={setCompanyDialogOpen}
          onDisplayNameEdit={handleDisplayNameEdit}
        />
        <QuickStatsCard />

        <Card sx={{ mt: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => {
                setActiveTab(newValue);
                localStorage.setItem('customerShowActiveTab', newValue.toString());
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2 }}
            >
              <Tab label="Overview" />
              <Tab
                label={
                  <Badge
                    badgeContent={estimatesCount}
                    color="primary"
                    invisible={estimatesCount === 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: 0,
                        fontSize: '0.75rem',
                        minWidth: '20px',
                        height: '20px',
                      }
                    }}
                  >
                    <Box sx={{ pr: 2 }}>Estimates</Box>
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge
                    badgeContent={invoicesCount}
                    color="primary"
                    invisible={invoicesCount === 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: 0,
                        fontSize: '0.75rem',
                        minWidth: '20px',
                        height: '20px',
                      }
                    }}
                  >
                    <Box sx={{ pr: 2 }}>Invoices</Box>
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge
                    badgeContent={opportunitiesCount}
                    color="primary"
                    invisible={opportunitiesCount === 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: 0,
                        fontSize: '0.75rem',
                        minWidth: '20px',
                        height: '20px',
                      }
                    }}
                  >
                    <Box sx={{ pr: 2 }}>Opportunities</Box>
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge
                    badgeContent={tasksCount}
                    color="primary"
                    invisible={tasksCount === 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: 0,
                        fontSize: '0.75rem',
                        minWidth: '20px',
                        height: '20px',
                      }
                    }}
                  >
                    <Box sx={{ pr: 2 }}>Tasks</Box>
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge
                    badgeContent={addressesCount}
                    color="primary"
                    invisible={addressesCount === 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: 0,
                        fontSize: '0.75rem',
                        minWidth: '20px',
                        height: '20px',
                      }
                    }}
                  >
                    <Box sx={{ pr: 2 }}>Addresses</Box>
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge
                    badgeContent={contactLogsCount}
                    color="primary"
                    invisible={contactLogsCount === 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: 0,
                        fontSize: '0.75rem',
                        minWidth: '20px',
                        height: '20px',
                      }
                    }}
                  >
                    <Box sx={{ pr: 2 }}>Contact Log</Box>
                  </Badge>
                }
              />
              <Tab label="Portal" />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {activeTab === 0 && (
              <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <CustomerContactInformationCard
                      contactDialogOpen={contactDialogOpen}
                      setContactDialogOpen={setContactDialogOpen}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <CustomerCompanyInformationCard
                      companyDialogOpen={companyDialogOpen}
                      setCompanyDialogOpen={setCompanyDialogOpen}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="h6" color="primary">
                            Notes & Comments
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setNotesDialogOpen(true)}
                            sx={{ color: 'primary.main' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <RichTextField source="notes" label="Customer Notes" />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
            {activeTab === 1 && <EstimatesTabContent />}
            {activeTab === 2 && <InvoicesTabContent />}
            {activeTab === 3 && <OpportunityCards customerId={record?.id ? String(record.id) : undefined} onOpportunityChange={fetchAllCounts} />}
            {activeTab === 4 && <TasksTabContent />}
            {activeTab === 5 && <AddressTableWrapper />}
            {activeTab === 6 && <ContactLogTableWrapper />}
            {activeTab === 7 && <CustomerPortalTab />}
          </Box>
        </Card>

        {record && (
          <>
            <ContactInfoDialog
              open={contactDialogOpen}
              onClose={() => setContactDialogOpen(false)}
              record={record}
            />
            <LocationZoneDialog
              open={locationZoneDialogOpen}
              onClose={() => setLocationZoneDialogOpen(false)}
              record={record}
            />
            <NotesDialog
              open={notesDialogOpen}
              onClose={() => setNotesDialogOpen(false)}
              record={record}
            />
            <DisplayNameDialog
              open={displayNameDialogOpen}
              onClose={() => setDisplayNameDialogOpen(false)}
              value={displayNameValue}
              onChange={setDisplayNameValue}
              onSave={handleDisplayNameSave}
            />
          </>
        )}
      </Box>
    </Container>
  );
};

const CustomerTitle = () => {
  const record = useRecordContext();
  if (!record) return 'Loading...';

  return (
    record.displayName ||
    `${record.firstName || ''} ${record.lastName || ''}`.trim() ||
    record.company ||
    'Customer'
  );
};

export const CustomerShow = () => (
  <Show actions={false} title={<CustomerTitle />}>
    <ResourceBreadcrumbsWithActions hideEdit={true} hideDelete={true} />
    <CustomerShowContent />
  </Show>
);
